import { BaseInput } from '@onestep/coreui/BaseInput';
import { Button } from '@onestep/coreui/Button';
import { UpdateIcon } from '@onestep/coreui/icons/UpdateIcon';
import { toastError, toastSuccess } from '@onestep/coreui/Toast';
import { isAxiosError } from 'axios';
import React from 'react';
import { useForm } from 'react-hook-form';

import { getErrorMessage } from '@/utils/errorUtil';

import useLoggedUser from '../../hooks/useLoggedUser';
import useMixpanel from '../../hooks/useMixpanel';
import type { ProfileFormData } from './types';

interface ProfileCompletionFormProps {
  currentProfile?: {
    firstName?: string;
    lastName?: string;
  };
  onComplete?: () => void;
  showSuccessToast?: boolean;
  screen?: string;
}

const FIELDS = [
  { name: 'firstName', placeholder: 'First name', label: 'First name' },
  { name: 'lastName', placeholder: 'Last name', label: 'Last name' },
] as const;

export const ProfileCompletionForm = ({
  currentProfile,
  onComplete,
  showSuccessToast = true,
  screen,
}: ProfileCompletionFormProps) => {
  const { update, refetchProfile } = useLoggedUser();
  const { track, trackSuccess, trackFail } = useMixpanel({ screen });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ProfileFormData>({
    mode: 'onSubmit',
    defaultValues: {
      firstName: currentProfile?.firstName ?? '',
      lastName: currentProfile?.lastName ?? '',
    },
  });

  const onSubmit = async (data: ProfileFormData) => {
    track('profile_completion_modal_submit_attempt');

    try {
      await update(data);
      await refetchProfile();
      if (showSuccessToast) {
        toastSuccess({ content: 'Profile updated successfully!' });
      }
      trackSuccess('profile_completion_modal_submit_success');
      onComplete?.();
    } catch (error) {
      toastError({ content: 'Failed to update profile. Please try again.' });
      trackFail('profile_completion_modal_submit_fail', {
        status: isAxiosError(error) ? error.response?.status : undefined,
        errorMessage: getErrorMessage(error),
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6 w-full">
      <div className="flex flex-col gap-6 w-full">
        {FIELDS.map(({ name, placeholder, label }) => (
          <div key={name}>
            <BaseInput
              placeholder={placeholder}
              renderInputElement={(params) => (
                <input
                  {...params}
                  aria-label={label}
                  {...register(name, {
                    required: `Please enter ${label.toLowerCase()}`,
                    minLength: { value: 2, message: 'Should include at least 2 characters' },
                    pattern: {
                      value: /^[\p{L}\s'-]+$/u,
                      message:
                        'Use only letters (including accented characters), spaces, hyphens and apostrophes',
                    },
                  })}
                  className="w-full text-base outline-none border-none bg-transparent"
                />
              )}
            />
            {errors[name] && <p className="text-error-p2 text-xs mt-1">{errors[name]?.message}</p>}
          </div>
        ))}
      </div>

      <div className="flex justify-center">
        <Button
          type="submit"
          variant="primary"
          size="medium"
          label={isSubmitting ? 'Saving...' : 'Save'}
          leftIcon={isSubmitting ? UpdateIcon : undefined}
          disabled={isSubmitting}
          className={isSubmitting ? '[&_svg]:animate-spin' : undefined}
        />
      </div>
    </form>
  );
};
